<template>
  <div>
    <v-card class="d-flex flex-column" style="max-height: 90vh">
      <div style="flex-shrink: 0">
        <v-card-title class="d-flex justify-space-between align-center py-2">
          <span class="text-md font-bold">Digital Profile</span>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
      </div>

      <v-card-text style="overflow-y: auto">
        <div>
          <!-- Profile Header -->
          <v-row class="p-4">
            <v-col cols="6">
              <div class="d-flex align-center">
                <!-- Left side: Avatar -->
                <UserAvatar
                  :user="employee"
                  :size="100"
                  :show-name="false"
                  :avatar-style="{ width: '100px', height: '100px' }"
                  class="w-1"
                />

                <!-- Middle: Name, EmpId, Designation, Department -->
                <div class="ml-4">
                  <div class="text-h6 font-weight-bold">
                    {{ employee.firstName }} {{ employee.lastName }} / {{ employee.employeeId }}
                  </div>
                  <div class="text-body-2">
                    {{ employee.department?.departmentName || 'N/A' }} ,
                    {{ employee.designation?.designationName || 'N/A' }}
                  </div>
                </div>

                <v-spacer></v-spacer>

                <!-- Right side: Status -->
                <div class="text-body-2 text-gray-600 dark-text-color">
                  <strong>Status</strong><br />
                  <v-chip small :color="getStatusColor(employee.status)" outlined>
                    {{ employee.status }}
                  </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider></v-divider>

          <v-row class="mt-4">
            <v-col cols="12" class="p-0">
              <v-expansion-panels multiple accordion v-model="panel">
                <!-- Personal Information -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Personal Information</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Email</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.email || employee.maskedEmail || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Mobile</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.mobile || employee.maskedMobileNumber || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Date of Birth</v-list-item-title
                              >
                              <v-list-item-subtitle>{{
                                employee.dob ? formatDate(employee.dob) : 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Gender</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.gender || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">Role</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.wfmRole?.role || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Employee Type</v-list-item-title
                              >
                              <v-list-item-subtitle>{{
                                employee.employeeType || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Address Information -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Address Information</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Address</v-list-item-title
                              >
                              <v-list-item-subtitle>
                                {{ employee.addressLine1 || 'N/A'
                                }}<span v-if="employee.addressLine2"
                                  >, {{ employee.addressLine2 }}</span
                                >
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">City</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.city || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">State</v-list-item-title>
                              <v-list-item-subtitle>{{
                                employee.state || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Pin Code</v-list-item-title
                              >
                              <v-list-item-subtitle>{{
                                employee.pinCode || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Employment Information -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Employment Information</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold">CTC</v-list-item-title>
                              <v-list-item-subtitle>{{
                                formatCurrency(employee.employmentDetails?.ctc) || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Reporting Manager</v-list-item-title
                              >
                              <v-list-item-subtitle>{{
                                getManagerName(employee.reportesTo) || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                      <v-col cols="12" md="4" class="p-0">
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-content>
                              <v-list-item-title class="font-weight-bold"
                                >Work Location</v-list-item-title
                              >
                              <v-list-item-subtitle>{{
                                employee.employmentDetails?.workLocation || 'N/A'
                              }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <!-- Joining Details -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Joining Details</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Joined On</div>
                        <div class="subtitle-2">
                          {{ formatDate(employee.employmentDetails?.joiningDetails?.joinedOn) }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Joined As</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.joiningDetails?.joinedAs || 'N/A' }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Confirmation Date</div>
                        <div class="subtitle-2">
                          {{
                            formatDate(employee.employmentDetails?.joiningDetails?.confirmationDate)
                          }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Status</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.joiningDetails?.status || 'N/A' }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Probation Period</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.joiningDetails?.probationPeriod || 'N/A' }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Notice Period</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.joiningDetails?.noticePeriod || 'N/A' }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Referred By</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.joiningDetails?.referredBy || 'N/A' }}
                        </div></v-col
                      >
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Work History -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Work History</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div v-if="employee.employmentDetails?.workHistory?.length">
                      <div
                        v-for="(item, index) in employee.employmentDetails.workHistory"
                        :key="index"
                        class="mb-4 pa-3 border rounded grey lighten-5"
                      >
                        <v-row>
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Employer Name</div>
                            <div class="subtitle-2">{{ item.employerName || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Designation</div>
                            <div class="subtitle-2">{{ item.designation || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Duration</div>
                            <div class="subtitle-2">
                              {{ formatDate(item.joiningDate) }} - {{ formatDate(item.exitDate) }}
                            </div></v-col
                          >
                          <v-col cols="12"
                            ><div class="text-caption">Reason For Leaving</div>
                            <div class="subtitle-2">
                              {{ item.reasonForLeaving || 'N/A' }}
                            </div></v-col
                          >
                        </v-row>
                      </div>
                    </div>
                    <div v-else class="text-caption text-center">No Work History Available</div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Education -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Education</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div v-if="employee.employmentDetails?.education?.length">
                      <div
                        v-for="(item, index) in employee.employmentDetails.education"
                        :key="index"
                        class="mb-4 pa-3 border rounded grey lighten-5"
                      >
                        <v-row>
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Degree</div>
                            <div class="subtitle-2">{{ item.degree || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Institute</div>
                            <div class="subtitle-2">{{ item.institute || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Grade</div>
                            <div class="subtitle-2">{{ item.grade || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Area</div>
                            <div class="subtitle-2">
                              {{ item.qualificationArea || 'N/A' }}
                            </div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Period</div>
                            <div class="subtitle-2">
                              {{ formatDate(item.from) }} - {{ formatDate(item.to) }}
                            </div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Remarks</div>
                            <div class="subtitle-2">{{ item.remarks || 'N/A' }}</div></v-col
                          >
                        </v-row>
                      </div>
                    </div>
                    <div v-else class="text-caption text-center">
                      No Education Details Available
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Identity Details -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Identity Details</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div v-if="employee.employmentDetails?.identityDetails?.length">
                      <div
                        v-for="(item, index) in employee.employmentDetails.identityDetails"
                        :key="index"
                        class="mb-4 pa-3 border rounded grey lighten-5"
                      >
                        <v-row>
                          <v-col cols="12" md="4"
                            ><div class="text-caption">ID Type</div>
                            <div class="subtitle-2">{{ item.idType || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">ID Number</div>
                            <div class="subtitle-2">{{ item.idNumber || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Name As per ID</div>
                            <div class="subtitle-2">{{ item.nameAsperId || 'N/A' }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Expiry Date</div>
                            <div class="subtitle-2">{{ formatDate(item.expiryDate) }}</div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Issued By</div>
                            <div class="subtitle-2">
                              {{ item.issuedBy || 'N/A' }} ({{ formatDate(item.issueDate) }})
                            </div></v-col
                          >
                          <v-col cols="12" md="4"
                            ><div class="text-caption">Verification</div>
                            <div v-if="isPan(item.idType)">
                              <div v-if="item.isVerfied">
                                <v-icon color="success">mdi-check-circle</v-icon>
                              </div>
                              <div v-else class="d-flex align-center">
                                <span class="error--text subtitle-2 mr-2"
                                  >Not Verified
                                  <v-icon left color="error">mdi-close-circle</v-icon>
                                </span>
                                <v-btn
                                  small
                                  outlined
                                  color="error"
                                  class="normal-case"
                                  :loading="item.loading"
                                  :disabled="item.verifyFailedAttempt >= 3"
                                  @click="verifyPan(item)"
                                >
                                  Verify Now
                                </v-btn>
                                <v-btn
                                  v-if="item.verifyFailedAttempt >= 3"
                                  icon
                                  small
                                  class="ml-2"
                                  color="warning"
                                  @click="resetAttempts(item)"
                                  title="Reset Attempts (Test)"
                                >
                                  <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                              </div>
                              <div
                                v-if="item.verifyFailedAttempt >= 3"
                                class="text-caption error--text mt-1"
                              >
                                Max attempts reached
                              </div>
                            </div>
                            <div v-else-if="isAadhaar(item.idType)">
                              <div v-if="item.isVerfied">
                                <v-icon color="success">mdi-check-circle</v-icon>
                              </div>
                              <div v-else class="d-flex align-center">
                                <span class="error--text subtitle-2 mr-2"
                                  >Not Verified
                                  <v-icon left color="error">mdi-close-circle</v-icon>
                                </span>
                                <v-btn
                                  small
                                  outlined
                                  color="error"
                                  class="normal-case"
                                  :loading="item.loading"
                                  :disabled="item.verifyFailedAttempt >= 3"
                                  @click="initAadhaarVerification(item)"
                                >
                                  Verify Now
                                </v-btn>
                                <v-btn
                                  v-if="item.verifyFailedAttempt >= 3"
                                  icon
                                  small
                                  class="ml-2"
                                  color="warning"
                                  @click="resetAttempts(item)"
                                  title="Reset Attempts (Test)"
                                  v-tooltip="{ text: 'Reset Attempts' }"
                                >
                                  <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                              </div>
                              <div
                                v-if="item.verifyFailedAttempt >= 3"
                                class="text-caption error--text mt-1"
                              >
                                Max attempts reached
                              </div>
                            </div>
                            <div
                              v-else
                              class="subtitle-2"
                              :class="item.isVerfied ? 'success--text' : 'error--text'"
                            >
                              {{ item.isVerfied ? 'Verified' : 'Not Verified' }}
                            </div>
                          </v-col>

                          <template v-if="item.bankName || item.accountNumber">
                            <v-col cols="12" class="pb-0"
                              ><div class="text-caption font-weight-bold">
                                Linked Bank Details
                              </div></v-col
                            >
                            <v-col cols="12" md="4"
                              ><div class="text-caption">Bank</div>
                              <div class="subtitle-2">{{ item.bankName || 'N/A' }}</div></v-col
                            >
                            <v-col cols="12" md="4"
                              ><div class="text-caption">Account No</div>
                              <div class="subtitle-2">{{ item.accountNumber || 'N/A' }}</div></v-col
                            >
                            <v-col cols="12" md="4"
                              ><div class="text-caption">IFSC</div>
                              <div class="subtitle-2">{{ item.ifscCode || 'N/A' }}</div></v-col
                            >
                          </template>
                        </v-row>
                      </div>
                    </div>
                    <div v-else class="text-caption text-center">No Identity Details Available</div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Background Verification -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Background Verification</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Status</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.backgroundVerification?.status || 'N/A' }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Verified By</div>
                        <div class="subtitle-2">
                          {{
                            employee.employmentDetails?.backgroundVerification?.verifiedBy || 'N/A'
                          }}
                        </div></v-col
                      >
                      <v-col cols="12" md="4"
                        ><div class="text-caption">Verified On</div>
                        <div class="subtitle-2">
                          {{
                            formatDate(
                              employee.employmentDetails?.backgroundVerification?.verifiedOn
                            )
                          }}
                        </div></v-col
                      >
                      <v-col cols="12"
                        ><div class="text-caption">Remarks</div>
                        <div class="subtitle-2">
                          {{ employee.employmentDetails?.backgroundVerification?.remarks || 'N/A' }}
                        </div></v-col
                      >
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Contact & Emergency -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Contact & Emergency</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div class="mb-4">
                      <div class="subtitle-1 primary--text mb-2">Contact Details</div>
                      <v-row>
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Name</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.contact?.name || 'N/A' }}
                          </div></v-col
                        >
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Number</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.contact?.contactNumber || 'N/A' }}
                          </div></v-col
                        >
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Email</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.contact?.email || 'N/A' }}
                          </div></v-col
                        >
                        <v-col cols="12" md="12">
                          <div class="text-caption">Address</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.contact?.address?.addressLine1 || '' }}
                            {{ employee.employmentDetails?.contact?.address?.city || '' }}
                            {{ employee.employmentDetails?.contact?.address?.state || '' }}
                            {{ employee.employmentDetails?.contact?.address?.country || '' }}
                            {{ employee.employmentDetails?.contact?.address?.pinCode || '' }}
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                    <v-divider class="mb-4"></v-divider>
                    <div>
                      <div class="subtitle-1 primary--text mb-2">Emergency Contact</div>
                      <v-row>
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Name</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.emergencyContact?.name || 'N/A' }}
                          </div></v-col
                        >
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Number</div>
                          <div class="subtitle-2">
                            {{
                              employee.employmentDetails?.emergencyContact?.contactNumber || 'N/A'
                            }}
                          </div></v-col
                        >
                        <v-col cols="12" md="4"
                          ><div class="text-caption">Email</div>
                          <div class="subtitle-2">
                            {{ employee.employmentDetails?.emergencyContact?.email || 'N/A' }}
                          </div></v-col
                        >
                        <v-col cols="12" md="12">
                          <div class="text-caption">Address</div>
                          <div class="subtitle-2">
                            {{
                              employee.employmentDetails?.emergencyContact?.address?.addressLine1 ||
                              ''
                            }}
                            {{ employee.employmentDetails?.emergencyContact?.address?.city || '' }}
                            {{ employee.employmentDetails?.emergencyContact?.address?.state || '' }}
                            {{
                              employee.employmentDetails?.emergencyContact?.address?.country || ''
                            }}
                            {{
                              employee.employmentDetails?.emergencyContact?.address?.pinCode || ''
                            }}
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Remarks -->
                <v-expansion-panel>
                  <v-expansion-panel-header class="font-weight-bold"
                    >Remarks</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div class="subtitle-2">
                      {{ employee.employmentDetails?.remarks || 'No Remarks' }}
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>

    <!-- Aadhaar OTP Dialog -->
    <v-dialog v-model="aadhaarOtpDialog" persistent max-width="400">
      <v-card class="pa-4">
        <v-card-title class="headline-1">Enter Aadhaar OTP</v-card-title>
        <v-card-text>
          <div class="mb-4">An OTP has been sent to your Aadhaar-linked mobile number.</div>
          <PasscodeInput
            v-if="aadhaarOtpDialog"
            v-model="aadhaarOtp"
            :otpField="6"
            :autoFocus="true"
            @keyup.enter.native="submitAadhaarOtp"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="aadhaarOtpDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            class="white--text"
            :loading="aadhaarLoading"
            :disabled="!aadhaarOtp || aadhaarOtp.length !== 6"
            @click="submitAadhaarOtp"
          >
            Verify
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import PasscodeInput from '@/components/PasscodeInput.vue'

export default {
  name: 'DigitalProfile',
  components: {
    UserAvatar,
    PasscodeInput
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    employee: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      fallbackImage: require('@/assets/img/profileOther.png'),
      panel: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
      aadhaarOtpDialog: false,
      aadhaarOtp: '',
      aadhaarLoading: false,
      aadhaarRefId: null,
      currentAadhaarItem: null
    }
  },
  computed: {
    profileImageUrl() {
      if (this.employee?.systemUserId?.profileImage) {
        return `${process.env.VUE_APP_S3_BASE_URL}${this.employee.systemUserId.profileImage}`
      }
      return this.fallbackImage
    }
  },
  watch: {
    visible(newVal) {
      this.dialog = newVal
    }
  },
  methods: {
    closeDialog() {
      this.$emit('handleDigitalProfileClose')
    },
    onImageError() {
      // Image error handling is handled by the computed property
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatCurrency(amount) {
      if (!amount) return 'N/A'
      return `₹${amount.toLocaleString('en-IN')}`
    },
    getManagerName(manager) {
      if (!manager) return 'N/A'
      return `${manager.firstName} ${manager.lastName}`
    },
    getStatusColor(status) {
      const statusColors = {
        ACTIVE: 'success',
        INACTIVE: 'warning',
        CLOSED: 'error'
      }
      return statusColors[status] || 'grey'
    },
    isPan(type) {
      return type && type.toUpperCase().includes('PAN')
    },
    isAadhaar(type) {
      return (
        type && (type.toUpperCase().includes('AADHAAR') || type.toUpperCase().includes('ADHAR'))
      )
    },
    async verifyPan(item) {
      if (!item) return
      if (item.verifyFailedAttempt >= 3) return

      this.$set(item, 'loading', true)
      try {
        const payload = {
          pan: item.idNumber,
          name_as_per_pan: item.nameAsperId,
          date_of_birth: this.$dayjs(this.employee.dob).format('DD/MM/YYYY')
        }

        const { data } = await this.$axios.post('/kyc/verify-pan', payload)
        const response = data

        if (
          response &&
          response.code === 200 &&
          response.data &&
          response.data.status === 'valid'
        ) {
          this.$set(item, 'isVerfied', true)
          // Save response data keys directly to item's metaData
          this.$set(item, 'metaData', response.data || response)

          await this.updateEmploymentDetails()
          this.$emit('panVerified', item)
        } else {
          console.error('Verification failed', response)
          const attempts = (item.verifyFailedAttempt || 0) + 1
          this.$set(item, 'verifyFailedAttempt', attempts)
          await this.updateEmploymentDetails()
        }
      } catch (error) {
        console.error('Verification error', error)
        const attempts = (item.verifyFailedAttempt || 0) + 1
        this.$set(item, 'verifyFailedAttempt', attempts)
        await this.updateEmploymentDetails()
      } finally {
        this.$set(item, 'loading', false)
      }
    },
    async initAadhaarVerification(item) {
      if (!item) return
      if (item.verifyFailedAttempt >= 3) return

      this.currentAadhaarItem = item
      this.$set(item, 'loading', true)

      try {
        const payload = {
          aadhaar_number: item.idNumber
        }
        const { data } = await this.$axios.post('/kyc/aadhaar/otp', payload)

        if (data && data.data && data.data.reference_id) {
          this.aadhaarRefId = data.data.reference_id
          this.aadhaarOtp = ''
          this.aadhaarOtpDialog = true
        } else {
          console.error('Failed to send OTP', data)
          const attempts = (item.verifyFailedAttempt || 0) + 1
          this.$set(item, 'verifyFailedAttempt', attempts)
          await this.updateEmploymentDetails()
        }
      } catch (error) {
        console.error('Error sending Aadhaar OTP', error)
        const attempts = (item.verifyFailedAttempt || 0) + 1
        this.$set(item, 'verifyFailedAttempt', attempts)
        await this.updateEmploymentDetails()
      } finally {
        this.$set(item, 'loading', false)
      }
    },
    async submitAadhaarOtp() {
      if (!this.aadhaarOtp || !this.aadhaarRefId) return

      this.aadhaarLoading = true
      const item = this.currentAadhaarItem

      try {
        const payload = {
          reference_id: this.aadhaarRefId,
          otp: this.aadhaarOtp
        }

        const { data } = await this.$axios.post('/kyc/aadhaar/verify', payload)

        if (data && (data.code === 200 || data.status === 'SUCCESS' || data.success)) {
          this.$set(item, 'isVerfied', true)

          // Save response data keys directly to item's metaData
          this.$set(item, 'metaData', data.data || data)

          await this.updateEmploymentDetails()
          this.$emit('aadhaarVerified', item)
          this.aadhaarOtpDialog = false
        } else {
          console.error('Aadhaar verification failed', data)
          const attempts = (item.verifyFailedAttempt || 0) + 1
          this.$set(item, 'verifyFailedAttempt', attempts)
          await this.updateEmploymentDetails()
        }
      } catch (error) {
        console.error('Error verifying Aadhaar OTP', error)
        const attempts = (item.verifyFailedAttempt || 0) + 1
        this.$set(item, 'verifyFailedAttempt', attempts)
        await this.updateEmploymentDetails()
      } finally {
        this.aadhaarLoading = false
      }
    },
    async updateEmploymentDetails() {
      try {
        const id = this.employee.employmentDetails?._id
        if (!id) return
        const payload = {
          identityDetails: this.employee.employmentDetails.identityDetails
        }
        await this.$axios.put(`/emp/employment-details/${id}`, payload)
      } catch (error) {
        console.error('Error updating employment details:', error)
      }
    },
    async resetAttempts(item) {
      if (!item) return
      this.$set(item, 'verifyFailedAttempt', 0)
      this.$set(item, 'isVerfied', false)
      await this.updateEmploymentDetails()
      this.$toast.success('Attempts reset successfully')
    }
  }
}
</script>
